/**
 * BESTIX v5.0 — Magazine Article Card
 * Editorial-style article card with 3 variants
 */

import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ArticleCardProps {
  slug: string;
  title: string;
  subtitle?: string;
  excerpt: string;
  image: string;
  category: string;
  readTime: number;
  author?: string;
  publishedAt: string;
  featured?: boolean;
  variant?: 'default' | 'horizontal' | 'hero';
}

const categoryColors: Record<string, string> = {
  'manifesto': 'bg-[var(--color-coral)] text-white',
  'archetypes': 'bg-[var(--color-gold)] text-[var(--color-navy)]',
  'ticket-confidence': 'bg-emerald-500 text-white',
  'seat-snob': 'bg-purple-600 text-white',
  'venue-intel': 'bg-blue-600 text-white',
  'show-types': 'bg-amber-500 text-[var(--color-navy)]',
  'city-nights': 'bg-rose-500 text-white',
  'why-go': 'bg-[var(--color-navy)] text-white',
  'best-of': 'bg-[var(--color-gold)] text-[var(--color-navy)]',
  'culture': 'bg-[var(--color-navy)] text-[var(--color-gold)]',
};

export function ArticleCard({
  slug,
  title,
  subtitle,
  excerpt,
  image,
  category,
  readTime,
  author = 'BESTIX Editorial',
  publishedAt,
  featured = false,
  variant = 'default',
}: ArticleCardProps) {
  const categorySlug = category.toLowerCase().replace(/\s+/g, '-');
  const categoryColor = categoryColors[categorySlug] || 'bg-gray-800 text-white';

  if (variant === 'hero') {
    return (
      <Link
        to={`/magazine/${slug}`}
        className="group relative block aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden"
      >
        <img
          src={image}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/10" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <span className={cn('inline-block px-3 py-1 rounded text-xs font-bold uppercase tracking-wide mb-4', categoryColor)}>
            {category}
          </span>
          <h2 className="font-playfair text-2xl md:text-4xl lg:text-5xl text-white !text-white mb-3 max-w-4xl">
            {title}
          </h2>
          {subtitle && (
            <p className="font-playfair italic text-lg md:text-xl text-[var(--color-gold)] mb-4 max-w-2xl">
              {subtitle}
            </p>
          )}
          <p className="text-white/80 text-sm md:text-base max-w-2xl line-clamp-2 mb-4">
            {excerpt}
          </p>
          <div className="flex items-center gap-4 text-white/60 text-sm">
            <span>{author}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {readTime} min read
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/magazine/${slug}`}
        className="group flex gap-6 items-start"
      >
        <div className="relative w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className={cn('inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide mb-2', categoryColor)}>
            {category}
          </span>
          <h3 className="font-playfair text-lg text-[var(--color-navy)] group-hover:text-[var(--color-gold)] transition-colors line-clamp-2 mb-2">
            {title}
          </h3>
          <p className="text-gray-500 text-sm line-clamp-2 mb-2">{excerpt}</p>
          <div className="flex items-center gap-3 text-gray-400 text-xs">
            <span>{readTime} min</span>
            <span>•</span>
            <span>{publishedAt}</span>
          </div>
        </div>
      </Link>
    );
  }

  // Default card
  return (
    <Link
      to={`/magazine/${slug}`}
      className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
    >
      <div className="relative aspect-[16/10]">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-4 left-4">
          <span className={cn('px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wide', categoryColor)}>
            {category}
          </span>
        </div>
        {featured && (
          <div className="absolute top-4 right-4">
            <span className="px-2.5 py-1 bg-[var(--color-coral)] text-white rounded text-xs font-bold">
              FEATURED
            </span>
          </div>
        )}
      </div>
      
      <div className="p-5">
        <h3 className="font-playfair text-xl text-[var(--color-navy)] group-hover:text-[var(--color-gold)] transition-colors line-clamp-2 mb-2">
          {title}
        </h3>
        {subtitle && (
          <p className="font-playfair italic text-[var(--color-gold)] text-sm mb-2 line-clamp-1">
            {subtitle}
          </p>
        )}
        <p className="text-gray-500 text-sm line-clamp-3 mb-4">{excerpt}</p>
        
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 text-gray-400 text-xs">
            <Clock className="w-3.5 h-3.5" />
            <span>{readTime} min read</span>
          </div>
          <span className="flex items-center gap-1 text-[var(--color-gold)] text-sm font-medium group-hover:gap-2 transition-all">
            Read
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default ArticleCard;
